import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const values = [
  { icon: 'ri-shield-check-line', title: 'Integrity', desc: 'Transparency and ethical sourcing in every decision we make.' },
  { icon: 'ri-award-line', title: 'Excellence', desc: 'Modern standards, hygienic processing, and global best practices.' },
  { icon: 'ri-compass-3-line', title: 'Purpose', desc: 'Every challenge shaped our direction and reinforced our values.' },
  { icon: 'ri-heart-line', title: 'Trust', desc: 'Food is more than taste — it is connection and reliability.' },
];

const brands = [
  { name: 'Fassnut', tagline: 'Premium Makhana & Snacks', icon: 'ri-seedling-line', color: 'bg-amber-50 text-amber-600' },
  { name: 'Rajsip', tagline: 'Pure Indian Spices', icon: 'ri-sparkling-line', color: 'bg-orange-50 text-orange-600' },
  { name: 'Faastea', tagline: 'Premium Tea Blends', icon: 'ri-cup-line', color: 'bg-emerald-50 text-emerald-600' },
  { name: 'Booston', tagline: 'Energy Tea Range', icon: 'ri-drop-line', color: 'bg-teal-50 text-teal-600' },
  { name: 'Sachar', tagline: 'Traditional Pickles', icon: 'ri-goblet-line', color: 'bg-red-50 text-red-600' },
  { name: 'Watler', tagline: 'Pure Drinking Water', icon: 'ri-water-flash-line', color: 'bg-sky-50 text-sky-600' },
];

const stats = [
  { number: '9+', label: 'Premium Products' },
  { number: '5+', label: 'Years Experience' },
  { number: '10K+', label: 'Happy Customers' },
  { number: 'FSSAI', label: 'Certified' },
];

const timeline = [
  { year: '2019', title: 'The Beginning', desc: 'Faasley Foods was founded with a vision to bring authentic, hygienic Indian food products to every home.' },
  { year: '2020', title: 'First Brand Launch', desc: 'Launched Fassnut Makhana and Rajsip Spices — our first two flagship product lines.' },
  { year: '2022', title: 'Portfolio Expansion', desc: 'Added Booston Tea, Sachar Achar, and Watler to the growing Faasley family of brands.' },
  { year: '2024', title: 'Going National', desc: 'Expanded distribution across India with 10,000+ happy customers and counting.' },
];

export default function AboutPage() {
  useEffect(() => {
    const schemaData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/about#webpage`,
          url: `${SITE_URL}/about`,
          name: 'About Faasley Foods | Our Story, Mission & Brand Values',
          description: 'Learn about Faasley Foods — founded by Kamini Thakur with a mission to deliver premium, authentic Indian packaged food products. Integrity shapes our flavours.',
          isPartOf: { '@id': `${SITE_URL}/#website` },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'About', item: `${SITE_URL}/about` },
            ],
          },
        },
        {
          '@type': 'AboutPage',
          name: 'About Faasley Foods',
          url: `${SITE_URL}/about`,
          description: 'Faasleyfoods Pvt. Ltd. was founded with a single purpose: to deliver premium quality packaged food products that taste as good as homemade.',
          mainEntity: {
            '@type': 'Organization',
            name: 'Faasley Foods',
            legalName: 'Faasleyfoods Pvt. Ltd.',
            foundingDate: '2019',
            founder: {
              '@type': 'Person',
              name: 'Kamini Thakur',
              jobTitle: 'Founder & CEO',
            },
            numberOfEmployees: { '@type': 'QuantitativeValue', value: 10 },
            slogan: 'Good Food, Good Mood',
          },
        },
        {
          '@type': 'Person',
          name: 'Kamini Thakur',
          jobTitle: 'Founder & CEO',
          worksFor: {
            '@type': 'Organization',
            name: 'Faasleyfoods Pvt. Ltd.',
          },
          image: 'https://storage.readdy-site.link/project_files/c44a33b9-73a7-4f29-8c86-80a04dae53c1/5cb346ab-a7f1-4cf6-953f-cf38d742223f_Kamini.jpg?v=7ff1f630b615292716d05c68d8042a2b',
          description: 'Passionate entrepreneur with a strong background in business management and product development. Founder of Faasley Foods.',
        },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-about';
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);
    document.title = 'About Faasley Foods | Our Story, Mission & Brand Values';
    return () => {
      const el = document.getElementById('schema-about');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* ── Hero ── */}
      <div className="relative pt-[240px] md:pt-[260px] bg-amber-50/60">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Left: Text */}
            <div>
              <span className="inline-block bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-5">
                Our Story
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 mb-5 leading-tight">
                About Faasley Foods
              </h1>
              <p className="text-stone-500 text-base md:text-lg max-w-xl leading-relaxed mb-8">
                A brand born from passion, built on integrity, and delivered with love.
              </p>
              <div className="flex flex-wrap gap-6">
                {[
                  { number: '9+', label: 'Products' },
                  { number: '5+', label: 'Brands' },
                  { number: '10K+', label: 'Customers' },
                ].map((s) => (
                  <div key={s.label}>
                    <p className="font-display text-3xl font-bold text-amber-500">{s.number}</p>
                    <p className="text-stone-400 text-sm">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
            {/* Right: Founder image */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative w-72 md:w-80 lg:w-96">
                <div className="absolute inset-0 bg-amber-200/40 rounded-3xl translate-x-3 translate-y-3"></div>
                <div className="relative rounded-3xl overflow-hidden aspect-[3/4] border border-amber-100">
                  <img
                    src="https://storage.readdy-site.link/project_files/c44a33b9-73a7-4f29-8c86-80a04dae53c1/5cb346ab-a7f1-4cf6-953f-cf38d742223f_Kamini.jpg?v=7ff1f630b615292716d05c68d8042a2b"
                    alt="Kamini Thakur, Founder & CEO"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur-sm rounded-2xl px-5 py-4 border border-stone-100">
                  <p className="text-amber-600 text-xs font-bold uppercase tracking-widest mb-0.5">Founder &amp; CEO</p>
                  <p className="font-display font-bold text-stone-900 text-base">Kamini Thakur</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Subtle bottom border */}
        <div className="h-px bg-gradient-to-r from-transparent via-amber-200 to-transparent"></div>
      </div>

      {/* ── Founder + Story ── */}
      <section className="py-20 md:py-28 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">

            {/* Left: Kamini Photo */}
            <div className="relative">
              <div className="rounded-3xl overflow-hidden aspect-[3/4]">
                <img
                  src="https://storage.readdy-site.link/project_files/c44a33b9-73a7-4f29-8c86-80a04dae53c1/5cb346ab-a7f1-4cf6-953f-cf38d742223f_Kamini.jpg?v=7ff1f630b615292716d05c68d8042a2b"
                  alt="Kamini Thakur, Founder & CEO"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Name tag */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-5 border border-stone-100">
                <p className="text-amber-600 text-xs font-bold uppercase tracking-widest mb-1">Founder &amp; CEO</p>
                <h3 className="font-display text-xl font-bold text-stone-900">Kamini Thakur</h3>
                <p className="text-stone-400 text-sm mt-0.5">Faasley Foods Pvt. Ltd.</p>
              </div>
              {/* Floating badge */}
              <div className="absolute -top-4 -right-4 bg-amber-500 text-white rounded-2xl px-4 py-3 text-center">
                <p className="font-bold text-xl leading-none">9+</p>
                <p className="text-xs mt-0.5 text-amber-100">Brands</p>
              </div>
            </div>

            {/* Right: Story Content */}
            <div className="flex flex-col gap-8">
              <div>
                <p className="text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-4">Our Mission</p>
                <h2 className="font-display text-4xl md:text-5xl font-bold text-stone-900 leading-tight mb-6">
                  Faasley – Good Food,<br />Good Mood
                </h2>
                <p className="text-stone-500 text-base leading-relaxed mb-4">
                  Faasleyfoods Pvt. Ltd. was founded with a single purpose: to deliver premium quality packaged food products that taste as good as homemade. We believe that everyone deserves access to authentic, hygienic, and delicious Indian food products.
                </p>
                <p className="text-stone-500 text-base leading-relaxed">
                  Kamini Thakur, the visionary behind Faasley Foods, is a passionate entrepreneur with a strong background in business management and product development. Her journey started with a deep understanding of market needs and a desire to create something authentic and meaningful.
                </p>
              </div>

              {/* Brand Story Card */}
              <div className="bg-amber-50 border border-amber-100 rounded-3xl p-8">
                <p className="text-amber-600 text-xs font-bold uppercase tracking-widest mb-3">The Journey</p>
                <h3 className="font-display text-xl font-bold text-stone-900 mb-4 leading-snug">
                  FAASLEY is not just a brand — it is a journey of values, courage, and transformation.
                </h3>
                <p className="text-stone-500 text-sm leading-relaxed mb-5">
                  What started as a partnership eventually evolved into a turning point. Choosing integrity over convenience, the founder stepped forward to build a brand rooted in honesty, professionalism, and authenticity.
                </p>
                <blockquote className="border-l-2 border-amber-400 pl-4 text-amber-600 italic text-sm">
                  "Integrity Shapes Our Flavours"
                </blockquote>
              </div>

              {/* Values Grid */}
              <div className="grid grid-cols-2 gap-4">
                {values.map((val) => (
                  <div key={val.title} className="bg-stone-50 rounded-2xl p-5">
                    <div className="w-9 h-9 flex items-center justify-center mb-3">
                      <i className={`${val.icon} text-amber-500 text-xl`}></i>
                    </div>
                    <p className="font-semibold text-stone-900 text-sm mb-1">{val.title}</p>
                    <p className="text-stone-400 text-xs leading-relaxed">{val.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <section className="bg-amber-50 py-14 border-y border-amber-100">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-display text-4xl md:text-5xl font-bold text-amber-500 mb-2">{stat.number}</p>
                <p className="text-stone-400 text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Timeline ── */}
      <section className="py-20 md:py-28 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="mb-14">
            <p className="text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Journey</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-stone-900">Milestones</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {timeline.map((item, i) => (
              <div key={i} className="relative bg-white rounded-3xl p-7 border border-stone-100">
                <div className="inline-block bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-4">
                  {item.year}
                </div>
                <h3 className="font-display font-bold text-stone-900 text-lg mb-2">{item.title}</h3>
                <p className="text-stone-400 text-sm leading-relaxed">{item.desc}</p>
                <div className="absolute top-7 right-7 text-stone-100 font-display font-bold text-5xl leading-none select-none">
                  {String(i + 1).padStart(2, '0')}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Our Brands ── */}
      <section className="py-20 md:py-28 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-14">
            <div>
              <p className="text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-3">Portfolio</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-stone-900">Our Brands</h2>
            </div>
            <p className="text-stone-400 text-sm max-w-sm md:text-right">
              A curated family of premium food brands under Faasleyfoods Pvt. Ltd.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {brands.map((brand, i) => (
              <div key={i} className="bg-stone-50 rounded-2xl p-6 flex flex-col items-center text-center hover:bg-amber-50 transition-colors group cursor-pointer border border-transparent hover:border-amber-100">
                <div className={`w-12 h-12 flex items-center justify-center rounded-2xl mb-4 ${brand.color.split(' ')[0]}`}>
                  <i className={`${brand.icon} text-xl ${brand.color.split(' ')[1]}`}></i>
                </div>
                <h3 className="font-display font-bold text-stone-900 text-sm mb-1">{brand.name}</h3>
                <p className="text-stone-400 text-xs leading-snug">{brand.tagline}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-amber-50/60 border-t border-amber-100">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-4">Get In Touch</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-stone-900 leading-tight mb-4">
                Want to Partner<br />With Us?
              </h2>
              <p className="text-stone-400 text-base leading-relaxed max-w-md">
                Whether you're a retailer, distributor, or investor — we'd love to connect and explore how we can grow together.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap"
              >
                Contact Us
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/investors"
                className="inline-flex items-center justify-center gap-2 border border-stone-200 text-stone-700 hover:bg-white font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap"
              >
                Investor Info
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
